import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import { TextSeeMore } from 'Components/frameworks/text-see-more/TextSeeMore';
import { Title } from 'Components/frameworks/title/Title';
import ratios from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { truncate } from 'Components/utils/string/string.util';
import { ProductIntroductionActions } from 'Components/widgets/product-introduction-actions/ProductIntroductionActions';
import { ProductLogo } from 'Components/widgets/product-logo/ProductLogo';
import { useTracking } from 'Tracking/tracking-api/useTracking';
type Props = {
  className?: string;
  iconName: IconName;
  imageSrc?: string;
  isExclusiveCollection?: boolean;
  productCountry?: string;
  productName?: string;
  ratingImage?:
    | {
        alt: string;
        src: string;
      }
    | null
    | undefined;
  reduceLabel?: string;
  seasonLink:
    | {
        iconName?: IconName;
        label: string;
        url: string;
      }
    | null
    | undefined;
  seeMoreLabel?: string;
  text?: string;
  title?: string;
};
export const ProductIntroduction = ({
  className,
  iconName,
  isExclusiveCollection,
  imageSrc,
  productCountry,
  productName,
  ratingImage,
  reduceLabel,
  seasonLink,
  seeMoreLabel,
  text,
  title,
}: Props) => {
  const { isDesktop } = useResponsive();
  const track = useTracking();

  if (!title) {
    return null;
  }

  const seeMoreSize = isDesktop ? 120 : 90;
  const top = (
    <div className="relative inset-x-0 -top-[6.25rem] z-1 mx-auto -mb-100 w-160 rounded-12 bg-white p-15 pt-20 shadow-subtle lg:absolute lg:-top-[3.75rem] lg:mb-0 lg:w-180">
      <ProductLogo
        className="mb-30"
        iconName={iconName}
        isExclusiveCollection={isExclusiveCollection}
        productCountry={productCountry}
        productName={productName}
      />
      <ProductIntroductionActions ratingImage={ratingImage} />
    </div>
  );
  const seasonSwitch = seasonLink && seasonLink.url && (
    <DynamicLink
      className="inline-flex items-center justify-center gap-10 rounded-pill p-15 text-14 font-bold leading-1 text-black shadow-subtle transition-colors duration-250 ease-in hocus:text-black-active lg:text-16"
      onClick={() =>
        track({
          actionGA: 'blockInfos',
          labelGA: 'switchSeason',
        })
      }
      url={seasonLink.url}
    >
      <Icon name={seasonLink.iconName} width={isDesktop ? '1.375rem' : '1rem'} />
      {seasonLink.label}
    </DynamicLink>
  );
  const content = (
    <Fragment>
      <Title className="mb-20 font-brand text-28 font-bold leading-tight md:text-44" theme="none">
        {truncate(title, 50)}
      </Title>
      <TextSeeMore
        hasNoScrollOnCollapse
        isCollapseEnabled={!isDesktop}
        seeLessLabel={reduceLabel}
        seeMoreLabel={seeMoreLabel}
        seeMoreSize={seeMoreSize}
        text={text}
        theme="text-sans-n3"
      />
    </Fragment>
  );
  return isDesktop ? (
    <article className={classnames('relative isolate mt-60 pt-20', className)}>
      {top}
      <div className="flex">
        <div className="relative z-0 w-1/2 overflow-hidden rounded-12">
          <Image imageUrl={imageSrc} ratioKey={ratios.R_63_BY_58} ratioDisplay={1 / 2} />
        </div>
        <div className="relative flex w-1/2 items-center py-60">
          <div className="absolute end-0 top-0">{seasonSwitch}</div>
          <div className="ms-auto w-5/6 ps-20">{content}</div>
        </div>
      </div>
    </article>
  ) : (
    <article className={className}>
      <div className="mb-40 pt-20">
        <div className="mx-auto max-w-400 overflow-hidden rounded-12">
          <Image imageUrl={imageSrc} ratioKey={ratios.SQUARE} />
        </div>
        {top}
        <div className="flex justify-center pt-15">{seasonSwitch}</div>
      </div>
      {content}
    </article>
  );
};
